import React from 'react';
import { useTranslations } from 'next-intl';
import type { AvgTotalRating } from '@/lib/types/customer/restaurant/AvgTotalRating';
import GenerateStar from './GenerateStar';
import ProgressBar from './ProgressBar';
import { DeliveryMini, Location, Pickup } from '@ui/Icons';
import { cn } from '@/lib/utils';
import STRINGS, { STOREFRONT } from '@/lib/utils/translation';
type Props = {
  avgTotalRating: AvgTotalRating;
  rounded?: boolean;
  storeName: string;
};
export default function OverallRatingCard({
  avgTotalRating,
  rounded,
  storeName,
}: Props) {
  const ratingTextStyles = {
    background: 'linear-gradient(90deg, #F5375C 0%, #C33076 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };
  const t = useTranslations();
  return (
    <div
      className={cn(
        'mt-[20px] grid grid-cols-1 gap-y-[20px] bg-primary bg-opacity-[0.07] p-[40px] md:grid-cols-2',
        rounded && 'rounded-[10px]'
      )}
    >
      <div className="">
        <span className="text-h6 font-bold text-title">
          {t(STOREFRONT.STORE_PROFILE + '.overall_score')}
        </span>
        <div className="flex items-center gap-x-[12px]">
          <span className="text-[74px] font-black" style={ratingTextStyles}>
            {avgTotalRating?.formatted.all}
          </span>
          <div className="flex flex-col gap-y-[6px]">
            <span className="flex">
              <GenerateStar numberOfStars={avgTotalRating?.original.all} />
            </span>
            <div>
              {t(STRINGS.COMMON + '.from')}{' '}
              <span className="underline underline-offset-4">
                {avgTotalRating?.counts.all} {t(STRINGS.COMMON + '.reviews')}
              </span>
            </div>
          </div>
        </div>
        <p className="max-w-[70%]">
          {t(STOREFRONT.STORE_PROFILE + '.rating_description')}{' '}
          <span className="font-semibold text-primary">{storeName}</span>
        </p>
      </div>

      <div className="grid h-fit w-fit grid-cols-2 place-content-end place-items-center gap-x-[10px] gap-y-[10px] text-neutral-400">
        <span className="col-span-2 place-self-start text-h6 font-bold text-title">
          {t(STOREFRONT.STORE_PROFILE + '.ratings')}
        </span>

        <ProgressBarWithCount
          rating={avgTotalRating?.original.food_rating}
          counts={avgTotalRating?.counts.food_rating}
          name={t(STOREFRONT.STORE_PROFILE + '.food')}
          icon={<FoodIcon />}
        />

        <ProgressBarWithCount
          counts={avgTotalRating?.counts.service_rating}
          rating={avgTotalRating?.original.service_rating}
          name={t(STOREFRONT.STORE_PROFILE + '.service')}
          icon={<Pickup />}
        />

        <ProgressBarWithCount
          rating={avgTotalRating?.original.delivery_rating}
          counts={avgTotalRating?.counts.delivery_rating}
          name={t(STOREFRONT.STORE_PROFILE + '.delivery')}
          icon={<DeliveryMini />}
        />

        <ProgressBarWithCount
          counts={avgTotalRating?.counts.location_rating}
          rating={avgTotalRating?.original.location_rating}
          name={t(STOREFRONT.STORE_PROFILE + '.location')}
          icon={<Location />}
        />
      </div>
    </div>
  );
}

const ProgressBarWithCount = ({
  counts,
  rating,
  name,
  icon,
}: {
  counts: number;
  rating: number;
  name: string;
  icon: React.JSX.Element;
}) => {
  return (
    <>
      {counts && counts !== 0 ? (
        <>
          <span className="flex items-center gap-x-[8px] place-self-start">
            {icon}
            <p className="capitalize text-paragraph">{name}</p>
          </span>
          <div className="flex items-center gap-x-[15px] place-self-start text-title">
            <ProgressBar rating={rating} />
            {counts}
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export const FoodIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5524 8.45031C10.5977 8.44512 10.6422 8.4369 10.6855 8.42585C11.1124 8.35134 11.5517 8.31249 12 8.31249C12.4483 8.31249 12.8876 8.35134 13.3145 8.42586C13.3578 8.4369 13.4023 8.44512 13.4476 8.45031C16.6283 9.06423 19.1018 11.6628 19.5285 14.9062H4.47151C4.89817 11.6628 7.37173 9.06423 10.5524 8.45031ZM14.5625 6.65852V6.53125C14.5625 5.116 13.4153 3.96875 12 3.96875C10.5847 3.96875 9.4375 5.116 9.4375 6.53125V6.65852C5.69093 7.69441 2.86445 10.946 2.45689 14.9145C1.96556 14.9778 1.58594 15.3977 1.58594 15.9062C1.58594 18.1844 3.43275 20.0312 5.71094 20.0312H18.2109C20.4568 20.0312 22.4141 18.2162 22.4141 15.9062C22.4141 15.3977 22.0344 14.9778 21.5431 14.9145C21.1355 10.946 18.3091 7.69441 14.5625 6.65852ZM12.5241 6.32656C12.4423 6.1171 12.2385 5.96875 12 5.96875C11.7615 5.96875 11.5577 6.1171 11.4759 6.32656C11.6494 6.31722 11.8242 6.31249 12 6.31249C12.1758 6.31249 12.3506 6.31722 12.5241 6.32656ZM3.83546 16.9062H20.1453C19.7696 17.5671 19.0395 18.0312 18.2109 18.0312H5.71094C4.89885 18.0312 4.19316 17.5757 3.83546 16.9062Z"
        fill="#A5A5AA"
      />
    </svg>
  );
};
